import { Fragment, useMemo, useState } from "react";
import {
  List,
  ListItem,
  Typography,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Checkbox,
  Paper,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { exportWishlistItemsToCsv } from "../utils/exportToCsv";
import * as wishlistItemsService from "../services/wishlistItemsService";
import {
  useClaimedItemsQuery,
  useUnclaimItem,
  useTogglePurchased,
  useDeleteSurpriseItem,
} from "../hooks/useClaimedItems";
import { useSortOrder, useSetSortOrder } from "../hooks/useUsers";
import ItemThumbnail from "./ItemThumbnail";
import SurpriseItemDialog from "./SurpriseItemDialog";
import GoHomeButton from "./GoHomeButton";

const THUMBNAIL_SIZE = { xs: 48, sm: 60 };

export default function ClaimedItems({ userId, activeGroupId, onNavigateHome }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: items = [] } = useClaimedItemsQuery(userId);
  const { data: sortOrder = "desc" } = useSortOrder(userId);
  const setSortOrderMutation = useSetSortOrder(userId);
  const unclaimMutation = useUnclaimItem(userId);
  const togglePurchasedMutation = useTogglePurchased(userId);
  const deleteSurpriseMutation = useDeleteSurpriseItem(userId);

  const [filter, setFilter] = useState("all"); // all | purchased | not_purchased
  const [unclaimingIds, setUnclaimingIds] = useState(() => new Set());

  // ---- Surprise Item delete flow ----
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingIds, setDeletingIds] = useState(() => new Set());

  const handleSortChange = async (e) => {
    const value = e.target.value;
    try {
      await setSortOrderMutation.mutateAsync({ userId, order: value });
    } catch {
      // Error already logged in claimedItemsService.
    }
  };

  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const diff = new Date(a.claimed_at) - new Date(b.claimed_at);
      return sortOrder === "asc" ? diff : -diff;
    });
    return copy;
  }, [items, sortOrder]);

  const filteredItems = useMemo(() => {
    if (filter === "purchased") return sortedItems.filter((i) => !!i.purchased_at);
    if (filter === "not_purchased") return sortedItems.filter((i) => !i.purchased_at);
    return sortedItems;
  }, [sortedItems, filter]);

  // Groups items by true recipient (surprise label, then the child claimed for, then
  // the list owner as a fallback), alphabetically by group so items bought for the same
  // person stay together — items within a group keep the date order from filteredItems.
  const groupedFilteredItems = useMemo(() => {
    const recipientOf = (i) => i.surprise_recipient_label || i.claimed_for_child_name || i.names?.name || "";
    const groups = new Map();
    for (const item of filteredItems) {
      const recipient = recipientOf(item);
      if (!groups.has(recipient)) groups.set(recipient, []);
      groups.get(recipient).push(item);
    }
    return [...groups.entries()].sort((a, b) =>
      a[0].localeCompare(b[0], undefined, { sensitivity: "base" })
    );
  }, [filteredItems]);

  const estimatedTotal = useMemo(() => {
    return items.reduce((sum, i) => sum + (Number(i.price) || 0), 0);
  }, [items]);

  const purchasedTotal = useMemo(() => {
    return items.reduce(
      (sum, i) => sum + (i.purchased_at ? Number(i.price) || 0 : 0),
      0
    );
  }, [items]);

  const visibleTotal = useMemo(() => {
    return filteredItems.reduce((sum, i) => sum + (Number(i.price) || 0), 0);
  }, [filteredItems]);

  const unclaim = async (id) => {
    if (unclaimingIds.has(id)) return;

    setUnclaimingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    // Look up the row's real claimed_by so the service's ownership guard is actually armed here
    // (rather than trivially satisfied by passing userId for both sides of the comparison).
    const target = items.find((i) => i.id === id);
    try {
      await unclaimMutation.mutateAsync({ itemId: id, currentClaimantId: target?.claimed_by, userId });
    } catch {
      // Error already logged in claimedItemsService, or the claimant guard rejected this locally.
    }

    setUnclaimingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const togglePurchased = async (item) => {
    try {
      await togglePurchasedMutation.mutateAsync({ itemId: item.id, purchased: !item.purchased_at });
    } catch {
      // Error already logged in claimedItemsService.
    }
  };

  // ---- Surprise Item: permanent delete ----
  const confirmDeleteSurprise = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    if (deletingIds.has(target.id)) return;

    setDeletingIds((prev) => {
      const next = new Set(prev);
      next.add(target.id);
      return next;
    });
    setDeleteTarget(null);

    try {
      await deleteSurpriseMutation.mutateAsync({ id: target.id, recipientNameId: target.surprise_recipient_name_id });
      wishlistItemsService.deleteThumbnail(target.thumbnail_url);
    } catch {
      // Error already logged in claimedItemsService.
    }

    setDeletingIds((prev) => {
      const next = new Set(prev);
      next.delete(target.id);
      return next;
    });
  };

  return (
    <Paper elevation={2} className="page-card" sx={{ p: { xs: 1.5, sm: 3 }, mt: 3, mb: 4 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ mb: 2, gap: 2 }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                value={filter}
                label="Filter"
                onChange={(e) => setFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="purchased">Purchased</MenuItem>
                <MenuItem value="not_purchased">Not Purchased</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sort by</InputLabel>
              <Select value={sortOrder} label="Sort by" onChange={handleSortChange}>
                <MenuItem value="desc">Newest first</MenuItem>
                <MenuItem value="asc">Oldest first</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Box>
            {filter === "all" ? (
              <>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                  Purchased
                </Typography>
                <Typography sx={{ fontWeight: 700, color: "success.main" }}>
                  ${purchasedTotal.toFixed(2)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  Estimated
                </Typography>
                <Typography sx={{ fontWeight: 700, color: "error.main" }}>
                  ${estimatedTotal.toFixed(2)}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                  Total
                </Typography>
                <Typography sx={{ fontWeight: 700, color: filter === "purchased" ? "success.main" : "error.main" }}>
                  ${visibleTotal.toFixed(2)}
                </Typography>
              </>
            )}
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} width={{ xs: "100%", md: "auto" }}>
          <GoHomeButton onNavigateHome={onNavigateHome} />
          <SurpriseItemDialog userId={userId} groupId={activeGroupId} />
          <Button
            variant="contained"
            onClick={() =>
              exportWishlistItemsToCsv(groupedFilteredItems.flatMap(([, groupItems]) => groupItems), false, "my-claimed-items.csv", {
                includePurchased: true,
                totalsMode: filter,
                purchasedTotal,
                estimatedTotal,
                totalCost: visibleTotal
              })
            }
          >
            Export to CSV
          </Button>
        </Stack>
      </Stack>

      <List sx={{ display: "grid", gap: 2 }}>
        {groupedFilteredItems.map(([recipient, groupItems]) => (
          <Fragment key={recipient || "unknown"}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary", mt: 1 }}>
              {recipient}
            </Typography>
            {groupItems.map((i) => {
              const isSurprise = !!i.surprise_recipient_label;
              return (
                <ListItem
                  key={i.id}
                  divider
                  disableGutters
                  sx={{
                    bgcolor: "background.paper",
                    borderRadius: 3,
                    boxShadow: "0 16px 30px rgba(0,0,0,0.06)",
                    p: { xs: 1, sm: 2 },
                    overflow: "hidden",
                  }}
                >
                  {isMobile ? (
                    <Box sx={{ width: "100%" }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <ItemThumbnail src={i.thumbnail_url} size={THUMBNAIL_SIZE} hideIfEmpty />
                        <Box sx={{ flex: 1 }} />
                        {isSurprise && (
                          <Chip label="Surprise" size="small" color="secondary" sx={{ flexShrink: 0 }} />
                        )}
                      </Stack>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mt: 1.5,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {i.url ? (
                          <Link href={i.url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#1976d2' }}>
                            {i.item_text}
                          </Link>
                        ) : i.item_text}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                        {i.price !== null && (
                          <Typography sx={{ fontWeight: 700 }}>
                            ${Number(i.price).toFixed(2)}
                          </Typography>
                        )}
                        {i.purchased_at && (
                          <Typography sx={{ color: "success.main", fontWeight: 600 }}>
                            · Purchased
                          </Typography>
                        )}
                      </Stack>
                      {isSurprise ? (
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                          For: {i.surprise_recipient_label}
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                          List: {i.names?.name || ""}
                        </Typography>
                      )}
                      {i.claimed_for_child_name && (
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                          For: {i.claimed_for_child_name}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                        Claimed {new Date(i.claimed_at).toLocaleDateString()}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: "center", justifyContent: "space-between" }}>
                        {isSurprise ? (
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => setDeleteTarget(i)}
                            disabled={deletingIds.has(i.id)}
                          >
                            {deletingIds.has(i.id) ? "Deleting..." : "Delete"}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() => unclaim(i.id)}
                            disabled={unclaimingIds.has(i.id)}
                          >
                            {unclaimingIds.has(i.id) ? "Unclaiming..." : "Unclaim"}
                          </Button>
                        )}
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography sx={{ color: i.purchased_at ? "success.main" : "common.white", fontWeight: 600 }}>
                            {i.purchased_at ? "Purchased" : "Purchased?"}
                          </Typography>
                          <Checkbox
                            checked={!!i.purchased_at}
                            onChange={() => togglePurchased(i)}
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  ) : (
                    <Box sx={{ width: "100%" }}>
                      <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="flex-start">
                        <ItemThumbnail src={i.thumbnail_url} size={THUMBNAIL_SIZE} />

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flexWrap: "wrap" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {i.url ? (
                                <Link href={i.url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#1976d2' }}>
                                  {i.item_text}
                                </Link>
                              ) : i.item_text}
                            </Typography>
                            {isSurprise && (
                              <Chip label="Surprise" size="small" color="secondary" sx={{ flexShrink: 0 }} />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                            {i.price !== null && (
                              <Typography sx={{ fontWeight: 700 }}>
                                ${Number(i.price).toFixed(2)}
                              </Typography>
                            )}
                            {i.purchased_at && (
                              <Typography sx={{ color: "success.main", fontWeight: 600 }}>
                                · Purchased
                              </Typography>
                            )}
                          </Stack>
                          {isSurprise ? (
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                              For: {i.surprise_recipient_label}
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                              List: {i.names?.name || ""}
                            </Typography>
                          )}
                          {i.claimed_for_child_name && (
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                              For: {i.claimed_for_child_name}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                            Claimed {new Date(i.claimed_at).toLocaleDateString()}
                          </Typography>
                        </Box>

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ flexShrink: 0, alignItems: "center" }}>
                          {isSurprise ? (
                            <Button
                              variant="contained"
                              color="error"
                              onClick={() => setDeleteTarget(i)}
                              disabled={deletingIds.has(i.id)}
                            >
                              {deletingIds.has(i.id) ? "Deleting..." : "Delete"}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              onClick={() => unclaim(i.id)}
                              disabled={unclaimingIds.has(i.id)}
                            >
                              {unclaimingIds.has(i.id) ? "Unclaiming..." : "Unclaim"}
                            </Button>
                          )}
                          <Typography sx={{ color: i.purchased_at ? "success.main" : "common.white", fontWeight: 600 }}>
                            {i.purchased_at ? "Purchased" : "Purchased?"}
                          </Typography>
                          <Checkbox
                            checked={!!i.purchased_at}
                            onChange={() => togglePurchased(i)}
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </ListItem>
              );
            })}
          </Fragment>
        ))}
      </List>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Surprise Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete this surprise item? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" onClick={confirmDeleteSurprise}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
